import { YesObject } from '@yes.technology/react-toolkit'
import fetchFilteraction from 'modules/filteraction/fetchFilteraction'
import api from 'utils/api'
import {
  ActivityModelStandardMultiselection,
  ActivityModelStandardUniselection
} from 'types/shared'
import { MULTISELECTION_SEPARATOR } from 'utils/constants'
import {
  DEFAULT_VALUE_PAGINATION_LIMIT,
  DefaultValueResolver,
  ResolverContext,
  fetchDefaultObjects
} from './helpers'

type SelectionActivityModel =
  ActivityModelStandardUniselection | ActivityModelStandardMultiselection

async function resolveWithStaticOptions(
  activityModel: SelectionActivityModel,
  defaultObjects: YesObject[],
  isMulti: boolean
) {
  const availableOptions = activityModel.selection_options!
  const availableUuids = availableOptions.map((opt) => opt.uuid)

  const matchingOptions = defaultObjects
    .filter((obj) => availableUuids.includes(obj.uuid))
    .map((obj) => {
      const defaultOption = availableOptions.find(
        (opt) => opt.uuid === obj.uuid
      )
      return {
        uuid: obj.uuid,
        des: defaultOption?.des ?? String(obj.des ?? '')
      }
    })

  if (!matchingOptions.length) return null

  const selectedOptions = isMulti ? matchingOptions : [matchingOptions[0]]

  return {
    uuid_activity_model: activityModel.uuid,
    des: selectedOptions.map((opt) => opt.uuid).join(MULTISELECTION_SEPARATOR),
    complement: selectedOptions
      .map((opt) => opt.des)
      .join(MULTISELECTION_SEPARATOR)
  }
}

async function resolveWithMergedQuery(
  activityModel: SelectionActivityModel,
  context: ResolverContext,
  isMulti: boolean
) {
  if (activityModel.information_request_filteraction_query_params?.length)
    return null

  const [defaultFilteraction, optionsFilteraction] = await Promise.all([
    fetchFilteraction(context.filteractionUuid, false, false),
    fetchFilteraction(
      activityModel.information_request_filteraction!,
      false,
      false
    )
  ])

  if (!defaultFilteraction || !optionsFilteraction) return null

  const mergedQuery = {
    $and: [
      JSON.parse(defaultFilteraction.query),
      JSON.parse(optionsFilteraction.query)
    ]
  }

  const objectclassUuid = (
    optionsFilteraction.filter_action_objectclass as YesObject[]
  )?.[0]?.uuid
  if (!objectclassUuid) return null

  const result = await api.postObjectsFilter(
    {
      query: mergedQuery,
      objectclass: objectclassUuid,
      orderby: [],
      pagination: { offset: 0, limit: DEFAULT_VALUE_PAGINATION_LIMIT },
      visualization: 'classic-list',
      skip_relations: true,
      skip_metadata: true,
      project_fields: { uuid: 1, des: 1 }
    },
    false,
    false
  )

  const objects = result.objects ?? []
  if (!objects.length) return null

  const selectedObjects = isMulti ? objects : [objects[0]]

  return {
    uuid_activity_model: activityModel.uuid,
    des: selectedObjects.map((obj) => obj.uuid).join(MULTISELECTION_SEPARATOR),
    complement: selectedObjects
      .map((obj) => String(obj.des ?? ''))
      .join(MULTISELECTION_SEPARATOR)
  }
}

async function resolveSelectionActivity(
  activityModel: SelectionActivityModel,
  context: ResolverContext,
  isMulti: boolean
) {
  if (activityModel.selection_options?.length) {
    const defaultObjects = await fetchDefaultObjects(context.filteractionUuid)
    return resolveWithStaticOptions(activityModel, defaultObjects, isMulti)
  }

  if (activityModel.information_request_filteraction) {
    return resolveWithMergedQuery(activityModel, context, isMulti)
  }

  return null
}

export const uniSelectionResolver: DefaultValueResolver = (
  activityModel,
  context
) =>
  resolveSelectionActivity(
    activityModel as SelectionActivityModel,
    context,
    false
  )

export const multiSelectionResolver: DefaultValueResolver = (
  activityModel,
  context
) =>
  resolveSelectionActivity(
    activityModel as SelectionActivityModel,
    context,
    true
  )
